import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { competenciaService, notaCompetenciaService } from 'services/competenciaService';
import { asignacionService } from 'services/matriculaService';
import { periodoService } from 'services/periodoService';
import { matriculaListService } from 'services/notaActividadService';
import {
  CompetenciaResponse,
  NotaCompetenciaResponse,
  CalificacionLetra,
  CALIFICACION_COLOR,
  CALIFICACION_LABEL,
  NotaCompetenciaItem,
} from 'types/competencia.types';
import { AsignacionItem, MatriculaResponse } from 'types/matricula.types';
import { PeriodoResponse } from 'types/periodo.types';

// ============================================================
// NotasCompetenciaPage — Registro de notas por competencia (DOCENTE)
// Flujo: 1. Seleccionar asignación → 2. Seleccionar período → 3. Registrar notas
// ============================================================

const SpinnerIcon = () => (
  <svg className="animate-spin w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

const LETRAS: CalificacionLetra[] = ['AD', 'A', 'B', 'C'];

const NIVEL_COLOR: Record<string, string> = {
  INICIAL:    'bg-amber-100 text-amber-700',
  PRIMARIA:   'bg-blue-100 text-blue-700',
  SECUNDARIA: 'bg-purple-100 text-purple-700',
};

// matriculaId → competenciaId → { calificacion, conclusion }
type NotasEdicion = Record<number, Record<number, { calificacion: CalificacionLetra; conclusion: string }>>;

const NotasCompetenciaPage: React.FC = () => {
  const { user } = useAuth();
  const userId = user?.idUsuario ?? 0;

  // Paso 1: Asignaciones
  const [asignaciones, setAsignaciones]     = useState<AsignacionItem[]>([]);
  const [loadingAsig, setLoadingAsig]       = useState(true);
  const [selectedAsig, setSelectedAsig]     = useState<AsignacionItem | null>(null);

  // Paso 2: Períodos
  const [periodos, setPeriodos]             = useState<PeriodoResponse[]>([]);
  const [periodoId, setPeriodoId]           = useState<number | null>(null);
  const [loadingPeriodos, setLoadingPeriodos] = useState(false);

  // Paso 3: Competencias + estudiantes + notas
  const [competencias, setCompetencias]     = useState<CompetenciaResponse[]>([]);
  const [estudiantes, setEstudiantes]       = useState<MatriculaResponse[]>([]);
  const [notasActuales, setNotasActuales]   = useState<NotaCompetenciaResponse[]>([]);
  const [loadingDatos, setLoadingDatos]     = useState(false);

  // Estado de edición
  const [notasEdicion, setNotasEdicion]     = useState<NotasEdicion>({});
  const [isDirty, setIsDirty]               = useState(false);

  // Estado de guardado
  const [guardando, setGuardando]           = useState(false);
  const [progresoGuardado, setProgresoGuardado] = useState<{ ok: number; total: number } | null>(null);
  const [guardadoOk, setGuardadoOk]         = useState(false);
  const [errorGuardar, setErrorGuardar]     = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      setLoadingAsig(true);
      try {
        const data = await asignacionService.getByDocente(user.idUsuario);
        setAsignaciones(data.filter(a => a.activo));
      } finally {
        setLoadingAsig(false);
      }
    };
    load();
  }, [user]);

  const handleSeleccionarAsig = useCallback(async (asig: AsignacionItem) => {
    if (isDirty && !window.confirm('Tienes cambios sin guardar. ¿Cambiar de asignación?')) return;
    setSelectedAsig(asig);
    setPeriodoId(null);
    setCompetencias([]);
    setEstudiantes([]);
    setNotasActuales([]);
    setNotasEdicion({});
    setIsDirty(false);
    setGuardadoOk(false);
    setErrorGuardar(null);
    setLoadingPeriodos(true);
    try {
      const ps = await periodoService.listarPorAnioEscolar(asig.anioEscolarId);
      setPeriodos(ps);
    } finally {
      setLoadingPeriodos(false);
    }
  }, [isDirty]);

  const handleSeleccionarPeriodo = useCallback(async (pid: number) => {
    if (!selectedAsig) return;
    if (isDirty && !window.confirm('Tienes cambios sin guardar. ¿Cambiar de período?')) return;
    setPeriodoId(pid);
    setLoadingDatos(true);
    setNotasEdicion({});
    setIsDirty(false);
    setGuardadoOk(false);
    setErrorGuardar(null);
    try {
      const [comps, ests, notas] = await Promise.all([
        competenciaService.listByCurso(selectedAsig.cursoId),
        matriculaListService.listarPorGradoSeccionAnio(
          selectedAsig.gradoId,
          selectedAsig.seccionId,
          selectedAsig.anioEscolarId,
        ),
        notaCompetenciaService.getBySeccion({
          gradoId:   selectedAsig.gradoId,
          seccionId: selectedAsig.seccionId,
          cursoId:   selectedAsig.cursoId,
          periodoId: pid,
        }),
      ]);
      setCompetencias(comps);
      setEstudiantes(ests);
      setNotasActuales(notas);

      // Inicializar edición solo con notas que ya existen en el backend
      const edicion: NotasEdicion = {};
      for (const est of ests) {
        edicion[est.idMatricula] = {};
        for (const comp of comps) {
          const nota = notas.find(
            n => n.matriculaId === est.idMatricula && n.competenciaId === comp.idCompetencia,
          );
          // Solo pre-rellenar si existe nota guardada; celdas nuevas quedan en 'A' como punto de partida
          edicion[est.idMatricula][comp.idCompetencia] = {
            calificacion: nota?.calificacion ?? 'A',
            conclusion:   nota?.conclusionDescriptiva ?? '',
          };
        }
      }
      setNotasEdicion(edicion);
    } finally {
      setLoadingDatos(false);
    }
  }, [selectedAsig, isDirty]);

  const setCelda = (matriculaId: number, competenciaId: number, field: 'calificacion' | 'conclusion', value: string) => {
    setIsDirty(true);
    setNotasEdicion(prev => ({
      ...prev,
      [matriculaId]: {
        ...prev[matriculaId],
        [competenciaId]: {
          ...prev[matriculaId]?.[competenciaId],
          [field]: value,
        },
      },
    }));
  };

  const handleGuardar = async () => {
    if (!selectedAsig || !periodoId) return;

    // Validar conclusiones obligatorias para calificación 'C'
    const sinConclusion: string[] = [];
    for (const est of estudiantes) {
      for (const comp of competencias) {
        const celda = notasEdicion[est.idMatricula]?.[comp.idCompetencia];
        if (celda?.calificacion === 'C' && !celda.conclusion.trim()) {
          sinConclusion.push(`${est.estudianteApellidos} — ${comp.nombre}`);
        }
      }
    }
    if (sinConclusion.length > 0) {
      const muestra = sinConclusion.slice(0, 3).join('; ');
      const extra   = sinConclusion.length > 3 ? ` (+${sinConclusion.length - 3} más)` : '';
      setErrorGuardar(`Conclusión descriptiva requerida para calificación C: ${muestra}${extra}`);
      return;
    }

    setGuardando(true);
    setErrorGuardar(null);
    setGuardadoOk(false);
    setProgresoGuardado({ ok: 0, total: estudiantes.length });

    // Guardar en paralelo — Promise.allSettled para no abortar ante el primer error
    const resultados = await Promise.allSettled(
      estudiantes.map(async est => {
        const calificaciones: NotaCompetenciaItem[] = competencias.map(comp => ({
          competenciaId:         comp.idCompetencia,
          calificacion:          notasEdicion[est.idMatricula]?.[comp.idCompetencia]?.calificacion ?? 'A',
          conclusionDescriptiva: notasEdicion[est.idMatricula]?.[comp.idCompetencia]?.conclusion || null,
        }));
        await notaCompetenciaService.registrarMasivo(
          { matriculaId: est.idMatricula, periodoId, calificaciones },
          userId,
        );
        setProgresoGuardado(prev => prev ? { ...prev, ok: prev.ok + 1 } : null);
      }),
    );

    const fallidos = resultados
      .map((r, i) => r.status === 'rejected' ? estudiantes[i] : null)
      .filter(Boolean) as MatriculaResponse[];

    setGuardando(false);
    setProgresoGuardado(null);

    if (fallidos.length === 0) {
      setGuardadoOk(true);
      setIsDirty(false);
      setTimeout(() => setGuardadoOk(false), 3000);
    } else {
      const nombres = fallidos.map(e => e.estudianteApellidos).slice(0, 3).join(', ');
      const extra   = fallidos.length > 3 ? ` (+${fallidos.length - 3} más)` : '';
      setErrorGuardar(
        `Error al guardar ${fallidos.length} estudiante(s): ${nombres}${extra}. ` +
        `Los demás fueron guardados correctamente.`,
      );
    }
  };

  const handleVolver = () => {
    if (isDirty && !window.confirm('Tienes cambios sin guardar. ¿Volver?')) return;
    setSelectedAsig(null);
    setPeriodoId(null);
    setIsDirty(false);
  };

  // ── Paso 1: seleccionar asignación ──────────────────────────
  if (loadingAsig) {
    return <div className="flex justify-center py-20"><SpinnerIcon /></div>;
  }

  if (!selectedAsig) {
    return (
      <div className="space-y-5">
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 rounded-2xl p-6 text-white">
          <h1 className="text-2xl font-bold">Notas por Competencia</h1>
          <p className="text-blue-200 mt-1">Selecciona un curso para registrar calificaciones por competencia</p>
        </div>
        {asignaciones.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm py-14 text-center">
            <p className="text-slate-400 text-sm">No tienes cursos asignados</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {asignaciones.map(a => (
              <button key={a.idAsignacion} onClick={() => handleSeleccionarAsig(a)}
                className="text-left bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all p-5">
                <h3 className="font-bold text-slate-800 text-base mb-1">{a.cursoNombre}</h3>
                <p className="text-sm text-slate-600">{a.gradoNombre} — Sección {a.seccionNombre}</p>
                <span className={`inline-block mt-2 text-xs font-bold px-2.5 py-1 rounded-full ${NIVEL_COLOR[a.gradoNivel] ?? 'bg-slate-100 text-slate-600'}`}>
                  {a.gradoNivel}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="bg-gradient-to-r from-blue-900 to-blue-700 rounded-2xl p-6 text-white">
        <button onClick={handleVolver}
          className="flex items-center gap-2 text-blue-200 hover:text-white text-sm mb-3 transition-colors">
          <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
            <path fillRule="evenodd" d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z" clipRule="evenodd"/>
          </svg>
          Volver
        </button>
        <h1 className="text-2xl font-bold">{selectedAsig.cursoNombre}</h1>
        <p className="text-blue-200 mt-0.5">
          {selectedAsig.gradoNombre} — Sección {selectedAsig.seccionNombre} — {selectedAsig.anio}
        </p>
      </div>

      {/* Selector de período */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Período</p>
        {loadingPeriodos ? (
          <SpinnerIcon />
        ) : (
          <div className="flex flex-wrap gap-2">
            {periodos.map(p => (
              <button key={p.idPeriodo} onClick={() => handleSeleccionarPeriodo(p.idPeriodo)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors
                  ${periodoId === p.idPeriodo
                    ? 'bg-blue-700 text-white shadow-sm'
                    : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}>
                {p.nombre}
              </button>
            ))}
            {periodos.length === 0 && <p className="text-slate-400 text-sm">No hay períodos configurados</p>}
          </div>
        )}
      </div>

      {/* Tabla de notas */}
      {periodoId && (
        <>
          {loadingDatos ? (
            <div className="flex justify-center py-12"><SpinnerIcon /></div>
          ) : estudiantes.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm py-12 text-center">
              <p className="text-slate-400 text-sm">No hay estudiantes matriculados en esta sección</p>
            </div>
          ) : competencias.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm py-12 text-center">
              <p className="text-slate-400 text-sm">No hay competencias configuradas para este curso</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center gap-4">
                <div>
                  <h2 className="font-bold text-slate-800">Registro de Competencias</h2>
                  <p className="text-slate-400 text-xs mt-0.5">
                    {estudiantes.length} estudiantes · {competencias.length} competencias
                    {isDirty && <span className="ml-2 text-amber-500 font-semibold">· Cambios sin guardar</span>}
                  </p>
                </div>
                <button onClick={handleGuardar} disabled={guardando}
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-xl px-4 py-2 transition-colors disabled:opacity-60 shrink-0">
                  {guardando ? (
                    <>
                      <SpinnerIcon />
                      {progresoGuardado && (
                        <span>{progresoGuardado.ok}/{progresoGuardado.total}</span>
                      )}
                    </>
                  ) : (
                    <>
                      <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                        <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd"/>
                      </svg>
                      Guardar cambios
                    </>
                  )}
                </button>
              </div>

              {guardadoOk && (
                <div className="px-6 py-2 bg-green-50 border-b border-green-100 text-green-700 text-sm font-semibold">
                  Calificaciones guardadas correctamente
                </div>
              )}
              {errorGuardar && (
                <div className="px-6 py-2 bg-red-50 border-b border-red-100 text-red-600 text-sm">
                  {errorGuardar}
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide sticky left-0 bg-slate-50 z-10 min-w-[200px]">
                        Estudiante
                      </th>
                      {competencias.map(c => (
                        <th key={c.idCompetencia} className="px-3 py-3 text-center text-xs font-bold text-slate-700 min-w-[140px] max-w-[180px]">
                          <span className="block truncate" title={c.nombre}>{c.nombre}</span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {estudiantes.map(est => (
                      <tr key={est.idMatricula} className="hover:bg-slate-50">
                        <td className="px-4 py-3 sticky left-0 bg-inherit z-10">
                          <p className="text-sm font-semibold text-slate-800 whitespace-nowrap">
                            {est.estudianteApellidos}, {est.estudianteNombres}
                          </p>
                          <p className="text-xs text-slate-400">{est.estudianteDni}</p>
                        </td>
                        {competencias.map(comp => {
                          const celda     = notasEdicion[est.idMatricula]?.[comp.idCompetencia];
                          const cal       = celda?.calificacion ?? 'A';
                          const conclusion = celda?.conclusion ?? '';
                          const sinConclusion = cal === 'C' && !conclusion.trim();
                          return (
                            <td key={comp.idCompetencia} className="px-3 py-2 align-top">
                              <div className="space-y-1.5">
                                <select
                                  value={cal}
                                  onChange={e => setCelda(est.idMatricula, comp.idCompetencia, 'calificacion', e.target.value)}
                                  className={`w-full px-2 py-1.5 rounded-lg border text-xs font-bold text-center outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer
                                    ${CALIFICACION_COLOR[cal]}`}
                                >
                                  {LETRAS.map(l => (
                                    <option key={l} value={l}>{l}</option>
                                  ))}
                                </select>
                                {cal === 'C' && (
                                  <textarea
                                    rows={2}
                                    value={conclusion}
                                    onChange={e => setCelda(est.idMatricula, comp.idCompetencia, 'conclusion', e.target.value)}
                                    className={`w-full px-2 py-1 rounded-lg border text-xs text-slate-700 outline-none focus:ring-2 resize-none
                                      ${sinConclusion
                                        ? 'border-red-400 bg-red-50 focus:ring-red-400'
                                        : 'border-red-200 bg-red-50 focus:ring-red-400'}`}
                                    placeholder="Conclusión descriptiva (requerida)..."
                                  />
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Leyenda */}
              <div className="px-6 py-3 border-t border-slate-100 flex flex-wrap gap-3">
                {LETRAS.map(l => (
                  <span key={l} className={`text-xs font-bold px-2.5 py-1 rounded-full ${CALIFICACION_COLOR[l]}`}>
                    {CALIFICACION_LABEL[l]}
                  </span>
                ))}
                <span className="text-xs text-slate-400 ml-auto">
                  Calificación C requiere conclusión descriptiva obligatoria
                </span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default NotasCompetenciaPage;
